import { useState } from 'react';
import { useRefresh } from 'react-admin';
import { hostUrl } from "../../../utils/url-manager";
import {read_cookie} from "sfcookies"

export type RefundResult = {
    status: 'success' | 'error',
    message: string
}

type UseInvoiceRefundProps = {
    isRefunding: boolean,
    refundError: string,
    processRefund: (id: string, type: string, amount: number, notes: string) => Promise<RefundResult>
}

// Said when the request fails in flight. The refund may still have reached Stripe, so this must
// not invite a straight retry.
export const REFUND_UNCONFIRMED_MESSAGE: string = "Unable to confirm the refund. Check this invoice's refunds before trying again.";

export const useInvoiceRefund: () => UseInvoiceRefundProps = (): UseInvoiceRefundProps => {
    const [isRefunding, setIsRefunding] = useState<boolean>(false);
    const [refundError, setRefundError] = useState<string>('');
    const refresh: () => void = useRefresh();

    async function processRefund(id: string, type: string, amount: number = 0, notes: string = ''): Promise<RefundResult> {
        setIsRefunding(true);
        setRefundError('');
        const token: string = read_cookie('auth');
        const request: Request = new Request(hostUrl + 'hq/customer/account/invoice/refund', {
            method: 'POST',
            body: JSON.stringify({
                id: id,
                type: type,
                amount: amount,
                notes: notes
            }),
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token': token
            })
        });
        try {
            const response: Response = await fetch(request);
            const data: any = await response.json();
            if (data?.status === 'success') {
                // The invoice's Refunds grid reads from the server, so it only shows the new
                // refund once the record is fetched again.
                refresh();
                return { status: 'success', message: data.message };
            }
            const message: string = data?.message || 'Unable to complete refund!';
            setRefundError(message);
            return { status: 'error', message };
        } catch (error) {
            console.log(error);
            setRefundError(REFUND_UNCONFIRMED_MESSAGE);
            return { status: 'error', message: REFUND_UNCONFIRMED_MESSAGE };
        } finally {
            setIsRefunding(false);
        }
    }

    return { isRefunding, refundError, processRefund }
}
